import React, { useContext, useCallback, useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { useTranslator } from '@jutro/locale';
import { useModal } from '@jutro/components';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { LocalDateUtil, JobUtil } from '@xengage/gw-portals-util-js';
import { SubmissionService, OrganizationService } from 'gw-capability-gateway';
import { AccountService } from 'gw-capability-gateway-policy';
import { FormModalPopover } from 'ml-components-platform-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { MLNewQuoteDialogModal, MLNewQuoteDialogModalMessages } from 'gw-capability-gateway-quoteandbind-common-react';
import metadata from './NewSubmissionPage.metadata.json5';
import styles from './NewSubmissionPage.module.scss';
import messages from '../../NewQuoteAccountSearch.messages';
import gatewayMessages from '../../../gateway.messages';

function NewSubmissionPage(props) {
    const modalApi = useModal();
    const viewModelService = useContext(ViewModelServiceContext);
    const {
        isAnExistingAccount,
        submissionVM,
        postalCode,
        updateSubmissionVM,
        producerCodeValue,
        isAgent,
        allOrganisationValue,
        authHeader,
        account
    } = props;
    const [productValue, updateProductValue] = useState([]);
    const [producerCodeOrgValue, updateProducerCodeValue] = useState([]);
    const [selectedOrganisationValue, updateOrganisation] = useState('');
    const [showErrors, updateShowErrors] = useState([false]);
    const { history } = props;
    const { lobQuoteURL } = appConfig;
    const { onValidate, isComponentValid } = useValidation('NewSubmissionPage');
    const translator = useTranslator();


    const showNewQuoteDialog = useCallback(() => {
        const accountVM = viewModelService.create(account, 'pc', 'edge.capabilities.gateway.account.dto.AccountDTO');
        const overrides = {
            model: accountVM,
            history
        };

        const componentProps = {
            title: translator(MLNewQuoteDialogModalMessages.startAQuote),
            overrideProps: overrides,
            CustomComponent: MLNewQuoteDialogModal,
            buttonType: 'outlined',
            showFooterOnCustomComponent: false,
            additionalPEStyle: 'customFooterModal',
            viewModelService,
            authHeader
        };
        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        );
    }, [account, history, translator, viewModelService]);

    const startSubmission = useCallback(() => {
        showNewQuoteDialog();
    }, [showNewQuoteDialog]);
    const refreshProducts = useCallback(
        async (newSubmissionVM) => {
            try {
                const submission = {
                    ...newSubmissionVM.value,
                    // product code is a required field and must have a value
                    productCode: _.get(newSubmissionVM, 'productCode.value', '')
                };
                const productCodeResponse = await AccountService.getAvailableProductsForAccount(
                    submission,
                    authHeader
                );
                const productCodeValues = productCodeResponse;
                const productCodeAvailableValue = productCodeValues
                    .filter((value) => !value.isRiskReserved)
                    .map(({ productCode, productName }) => {
                        return {
                            code: productCode,
                            name: productName
                        };
                    });
                updateProductValue(productCodeAvailableValue);
            } catch {
                updateProductValue([]);
                modalApi.showAlert({
                    title: gatewayMessages.modalError,
                    message: gatewayMessages.gatewayViewsModalErrorData,
                    status: 'error',
                    icon: 'mi-error-outline'
                }).catch(_.noop);
            }
        },
        [authHeader]
    );

    const getProducerCodesOrganization = useCallback(
        async (organisationCode) => {
            const producerCodeResponse = await OrganizationService.getProducerCodeForOrganization(
                organisationCode,
                authHeader
            );
            const producerCodeOrganisation = producerCodeResponse.map((value) => {
                return {
                    code: value.code,
                    name: value.displayValue
                };
            });
            updateProducerCodeValue(producerCodeOrganisation);
            _.set(submissionVM, 'producerCode', '');
            _.set(submissionVM, 'productCode', '');
            updateSubmissionVM(submissionVM);
        },
        [authHeader, submissionVM, updateSubmissionVM]
    );
    const handleValueOrganisation = (value) => {
        let organisationCode = {};

        const selectedOrganisation = value;
        allOrganisationValue.forEach((result) => {
            if (result.code === selectedOrganisation) {
                organisationCode = {
                    displayName: result.name,
                    publicID: result.code
                };
            }
        });
        getProducerCodesOrganization(organisationCode);
        updateOrganisation(value);
    };
    const writeValue = useCallback(
        (value, path) => {
            const newSubmissionVMClone = viewModelService.clone(submissionVM);
            _.set(newSubmissionVMClone, path, value);
            updateSubmissionVM(newSubmissionVMClone);
            if (
                (path === 'producerCode' || path === 'effectiveDate' || path === 'state')
                && !!(
                    _.get(newSubmissionVMClone, 'producerCode.value')
                    && _.get(newSubmissionVMClone, 'state.value.code')
                    && _.get(newSubmissionVMClone, 'effectiveDate.value')
                )
            ) {
                refreshProducts(newSubmissionVMClone);
                _.set(newSubmissionVMClone, 'productCode', '');
                updateSubmissionVM(newSubmissionVMClone);
            }
        },
        [refreshProducts, submissionVM, updateSubmissionVM, viewModelService]
    );
    const handleCancel = useCallback(() => {
        const accountNumber = _.get(submissionVM, 'accountNumber.value');
        modalApi.showConfirm({
            title: messages.cancelQuote,
            message: messages.cancelMessage,
            status: 'warning',
            icon: 'mi-error-outline'
        }).then((results) => {
            if (results === 'cancel') {
                return _.noop();
            }
            if (accountNumber) {
                history.push(`/accounts/${accountNumber}/summary`);
            } else {
                history.push('/');
            }
            return true;
        }, _.noop);
    }, [history, submissionVM]);
    const handleValidation = useCallback(() => {
        updateShowErrors(true);
    }, [updateShowErrors]);
    const producerdisable = producerCodeOrgValue.length > 0
        ? isAgent : producerCodeOrgValue.length === 0;
    const isAgentProducer = !isAgent ? producerdisable : !isAnExistingAccount;
    const overrideProps = {
        '@field': {
            labelPosition: 'left',
            showOptional: true
        },
        submissionProducerCode: {
            availableValues:
                _.isArray(producerCodeValue) && producerCodeValue.length > 0
                    ? producerCodeValue
                    : producerCodeOrgValue,
            disabled: isAgentProducer
        },
        submissionState: {
            disabled: !isAnExistingAccount
        },
        submissionDate: {
            minDate: LocalDateUtil.today()
        },
        submissionProductCode: {
            availableValues: productValue,
            disabled: !_.get(submissionVM, 'producerCode.value')
        },
        submissionOrganisation: {
            visible: !isAgent,
            availableValues: allOrganisationValue,
            onValueChange: handleValueOrganisation,
            value: selectedOrganisationValue
        },
        cancel: {
            disabled: !isAnExistingAccount
        },
        next: {
            disabled: !isAnExistingAccount
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            startSubmission: isComponentValid ? startSubmission : handleValidation,
            onCancel: handleCancel
        }
    };
    const readValue = (id, path) => {
        return readViewModelValue(metadata.pageContent, submissionVM, id, path, overrideProps);
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={submissionVM}
            overrideProps={overrideProps}
            resolveValue={readValue}
            onValidationChange={onValidate}
            onValueChange={writeValue}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
            showErrors={showErrors}
        />
    );
}
NewSubmissionPage.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    isAnExistingAccount: PropTypes.bool,
    submissionVM: PropTypes.shape({
        value: PropTypes.shape({})
    }),
    allOrganisationValue: PropTypes.arrayOf(PropTypes.string),
    postalCode: PropTypes.string,
    updateSubmissionVM: PropTypes.func,
    producerCodeValue: PropTypes.arrayOf(PropTypes.string),
    isAgent: PropTypes.string,
    authHeader: PropTypes.shape({})
};

NewSubmissionPage.defaultProps = {
    isAnExistingAccount: false,
    submissionVM: {},
    allOrganisationValue: [],
    postalCode: '',
    updateSubmissionVM: undefined,
    producerCodeValue: [],
    isAgent: false,
    authHeader: {}
};
export default withRouter(NewSubmissionPage);
